import AppModalContainer from '@nxsys/components/AppModalContainer';
import { privatePensionSchemeTypeOptions } from '@nxsys/constants/AppConst';
import { getTypeOfValueOptions } from '@nxsys/constants/AppConstants';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IPrivateIrelandPension } from '@nxsys/types/models/pension';
import { showCurrencyByCountry } from '@nxsys/utils';
import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PrivatePensionForm, {
  PrivatePensionFormRef,
  PrivatePensionFormValues,
} from './PrivatePensionForm';

type EditPrivatePensionModalProps = {
  isOpen: boolean;
  toggle: () => void;
  onSubmit: (params: PrivatePensionFormValues) => Promise<void>;
  data: IPrivateIrelandPension;
};

const EditPrivatePensionModal: React.FC<EditPrivatePensionModalProps> = ({
  isOpen,
  toggle,
  onSubmit,
  data,
}) => {
  const { messages } = useTypedIntl();
  const formRef = useRef<PrivatePensionFormRef>(null);
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const pensionTypeOptions = getTypeOfValueOptions(
    showCurrencyByCountry(payrollCountry),
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (values: PrivatePensionFormValues) => {
    try {
      setIsSubmitting(true);
      await onSubmit(values);
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AppModalContainer
      title={messages['common.editPrivatePensionSetting']}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.save'],
          buttonType: 'primary',
          onClick: () => formRef.current?.submit(),
          loading: isSubmitting,
          disabled: isSubmitting,
        },
      ]}
      minHeight="200px"
    >
      <PrivatePensionForm
        formRef={formRef}
        onSubmit={handleSubmit}
        defaultValues={{
          name: data.name,
          type:
            privatePensionSchemeTypeOptions.find(
              (item) => item.value === data.type,
            ) || null,
          reference: data.reference,
          eePensionValue: data.eePensionValue,
          eePensionType:
            pensionTypeOptions.find(
              (item) => item.value === data.eePensionType,
            ) || null,
          erPensionValue: data.erPensionValue,
          erPensionType:
            pensionTypeOptions.find(
              (item) => item.value === data.erPensionType,
            ) || null,
          avcPensionValue: data.avcPensionValue,
          avcPensionType:
            pensionTypeOptions.find(
              (item) => item.value === data.avcPensionType,
            ) || null,
        }}
      />
    </AppModalContainer>
  );
};

export default EditPrivatePensionModal;
